import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageSignature } from './MessageSignature';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Current staff user — drives the delete-own-only gate.
const currentUser = (() => {
  try { return JSON.parse(localStorage.getItem('doorly_admin_user') || '{}'); }
  catch { return {}; }
})();

const fmtWhen = (d) => {
  if (!d) return '';
  const then = new Date(d).getTime();
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// Module-scoped comment thread. Reused on the Property / Underwriting module
// cards (expanded row) and on the matching borrower-profile tab.
//   moduleType: 'property' | 'underwriting'
//   borrowerId: users.id the notes hang off
//   onCountChange?: (n) => void  — keeps a parent count badge in sync
export default function ModuleNotes({ moduleType, borrowerId, onCountChange }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` };

  const sync = useCallback((list) => {
    setNotes(list);
    if (onCountChange) onCountChange(list.length);
  }, [onCountChange]);

  useEffect(() => {
    if (!borrowerId) return;
    let alive = true;
    setLoading(true);
    axios.get(`${API}/api/admin/borrowers/${borrowerId}/module-notes`, { params: { module: moduleType }, headers })
      .then((r) => { if (alive) sync(r.data || []); })
      .catch(() => { if (alive) setNotes([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [borrowerId, moduleType]); // eslint-disable-line react-hooks/exhaustive-deps

  const addNote = async () => {
    if (!draft.trim() || saving) return;
    setSaving(true);
    setError('');
    try {
      const r = await axios.post(
        `${API}/api/admin/borrowers/${borrowerId}/module-notes`,
        { module: moduleType, note: draft.trim() },
        { headers }
      );
      sync([r.data, ...notes]);
      setDraft('');
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save note.');
    }
    setSaving(false);
  };

  const removeNote = async (noteId) => {
    try {
      await axios.delete(`${API}/api/admin/borrowers/${borrowerId}/module-notes/${noteId}`, { headers });
      sync(notes.filter((n) => n.id !== noteId));
    } catch (e) {
      setError(e.response?.data?.error || 'Could not delete note.');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Add box */}
      <div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') addNote(); }}
          placeholder="Add a note…"
          rows={2}
          style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
          <span style={{ fontSize: 11, color: error ? '#dc2626' : '#9ca3af' }}>{error || '⌘/Ctrl + Enter to post'}</span>
          {/* Doorly green (#acf44e) in both states — opacity drops when
              disabled so the button keeps the brand color instead of
              fading to a washed-out lighter shade. */}
          <button
            onClick={addNote}
            disabled={!draft.trim() || saving}
            style={{
              padding: '6px 16px', borderRadius: 8, border: 'none',
              background: '#acf44e', color: '#0f1a05',
              fontSize: 12, fontWeight: 700,
              opacity: !draft.trim() || saving ? 0.5 : 1,
              cursor: !draft.trim() || saving ? 'default' : 'pointer',
            }}>
            {saving ? 'Posting…' : 'Post Note'}
          </button>
        </div>
      </div>

      {/* Thread */}
      {loading ? (
        <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0' }}>Loading notes…</div>
      ) : notes.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0' }}>No notes yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {notes.map((n) => {
            const mine = currentUser?.id && Number(currentUser.id) === Number(n.created_by_id);
            const hasName = n.sender_first_name || n.sender_last_name || n.sender_name;
            return (
              <div key={n.id} style={{ background: '#f9fafb', border: '1px solid #eef0f2', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 13, color: '#1f2937', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{n.note}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 8 }}>
                  {/* Inline avatar + name + title + NMLS (shared with message bubbles) */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {hasName
                      ? <MessageSignature sender={n} />
                      : <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>{n.created_by || 'Staff'}</div>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, paddingBottom: 2 }}>
                    <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }} title={new Date(n.created_at).toLocaleString()}>
                      {fmtWhen(n.created_at)}
                    </span>
                    {mine && (
                      <button
                        onClick={() => removeNote(n.id)}
                        style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
